import React, { useMemo, useState } from 'react';
import { StyleSheet, Text, View, Pressable } from 'react-native';
import Svg, { Line } from 'react-native-svg';
import { FontFamily } from '../../../../../constants/fonts';

export interface ExecutionQuadrantPoint {
  label: string;
  adoptionPct: number;
  kcphIndex: number | null;
  withTool: boolean;
  volume?: number;
}

interface ExecutionQuadrantCardProps {
  title: string;
  subtitle?: string;
  points: ExecutionQuadrantPoint[];
}

const clamp = (v: number, min: number, max: number) => Math.max(min, Math.min(max, v));

const MIN_PLOT_HEIGHT = 190;
const BUBBLE_SIZE = 22;
// Bubble diameter range when sizing by volume — area (not diameter) scales linearly with volume
// so relative case counts read correctly at a glance, per standard bubble-chart convention.
const BUBBLE_MIN_SIZE = 14;
const BUBBLE_MAX_SIZE = 38;
const Y_MIN = 60;
const Y_MAX = 130;
const Y_TICKS = [130, 110, 90, 70];
// Space an above-bubble label needs (text height + gap) before it would clip past the plot's
// top edge into the header band row rendered just above the chart.
const LABEL_HEIGHT_PX = 20;
// Bubble color reflects adoption threshold, not the backend's withTool flag — 60%+ is blue, below is red.
const ADOPTION_COLOR_THRESHOLD = 60;

// Maps each point's volume to a bubble diameter (equal-area scale) — falls back to the fixed
// BUBBLE_SIZE for every point when the backend hasn't supplied volume on any of them yet.
const getBubbleSizes = (points: ExecutionQuadrantPoint[]): number[] => {
  const volumes = points.map((p) => (typeof p.volume === 'number' && p.volume > 0 ? p.volume : 0));
  const maxVolume = Math.max(...volumes);
  if (maxVolume <= 0) return points.map(() => BUBBLE_SIZE);
  return volumes.map((v) => {
    const ratio = Math.sqrt(v / maxVolume);
    return clamp(BUBBLE_MIN_SIZE + ratio * (BUBBLE_MAX_SIZE - BUBBLE_MIN_SIZE), BUBBLE_MIN_SIZE, BUBBLE_MAX_SIZE);
  });
};

// Spread out points that would otherwise render on top of one another (same/near-identical values).
// Keeps the original (un-spread) position alongside the placed one so the UI can draw a connector
// line back to the true data point whenever it had to be nudged.
const layoutPoints = (points: ExecutionQuadrantPoint[], plotWidth: number, plotHeight: number, sizes: number[]) => {
  const raw = points.map((point, index) => ({
    point,
    size: sizes[index] ?? BUBBLE_SIZE,
    x: (clamp(point.adoptionPct, 0, 100) / 100) * plotWidth,
    y: (clamp(((Y_MAX - (point.kcphIndex ?? 0)) / (Y_MAX - Y_MIN)) * 100, 0, 100) / 100) * plotHeight,
  }));

  const placed: { point: ExecutionQuadrantPoint; size: number; x: number; y: number; originX: number; originY: number }[] = [];
  raw.forEach(({ point, size, x: startX, y: startY }) => {
    let x = startX;
    let y = startY;
    let attempts = 0;
    const minDist = size + 10;
    while (attempts < 60 && placed.some((p) => Math.hypot(p.x - x, p.y - y) < (minDist + p.size) / 2)) {
      const angle = attempts * 0.9; // radians, spiral outward
      const radius = minDist * 0.6 * (1 + attempts * 0.18);
      x = clamp(startX + Math.cos(angle) * radius, 0, plotWidth);
      y = clamp(startY + Math.sin(angle) * radius, 0, plotHeight);
      attempts += 1;
    }
    placed.push({ point, size, x, y, originX: startX, originY: startY });
  });
  return placed;
};

const ExecutionQuadrantCard: React.FC<ExecutionQuadrantCardProps> = ({ title, subtitle, points }) => {
  const [plotWidth, setPlotWidth] = useState(0);
  const [plotHeight, setPlotHeight] = useState(MIN_PLOT_HEIGHT);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [showLabels, setShowLabels] = useState(true);

  const labelsToggle = (
    <View style={styles.toggleWrap}>
      <Text style={styles.toggleLabel}>Labels</Text>
      <View style={styles.toggleGroup}>
        <Pressable onPress={() => setShowLabels(true)} style={[styles.toggleBtn, showLabels && styles.toggleBtnActive]}>
          <Text style={[styles.toggleBtnText, showLabels && styles.toggleBtnTextActive]}>On</Text>
        </Pressable>
        <Pressable onPress={() => setShowLabels(false)} style={[styles.toggleBtn, !showLabels && styles.toggleBtnActive]}>
          <Text style={[styles.toggleBtnText, !showLabels && styles.toggleBtnTextActive]}>Off</Text>
        </Pressable>
      </View>
    </View>
  );

  // Check for no data condition
  if (!points || points.length === 0) {
    return (
      <View style={styles.card}>
        <View style={styles.headerRow}>
          <View style={styles.titleRow}>
            <Text style={styles.title}>{title}</Text>
            {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
          </View>
        </View>
        <View style={styles.emptyState}>
          <Text style={styles.emptyText}>No execution quadrant data available</Text>
        </View>
      </View>
    );
  }

  const bubbleSizes = useMemo(() => getBubbleSizes(points), [points]);

  const positioned = useMemo(
    () => (plotWidth > 0 ? layoutPoints(points, plotWidth, plotHeight, bubbleSizes) : []),
    [points, plotWidth, plotHeight, bubbleSizes],
  );

  return (
    <View style={styles.card}>
      <View style={styles.headerRow}>
        <View style={styles.titleRow}>
          <Text style={styles.title}>{title}</Text>
          {subtitle ? <Text style={styles.subtitle}>{subtitle}</Text> : null}
        </View>
        {labelsToggle}
      </View>

      <View style={styles.headerBands}>
        <Text style={styles.headerBandText}>PERFORMING WITHOUT THE TOOL</Text>
        <Text style={styles.headerBandTextRight}>ADOPTED + PERFORMING</Text>
      </View>

      <View style={styles.plotRow}>
        <View style={[styles.yAxisCol, { height: plotHeight }]}>
          {Y_TICKS.map((tick) => (
            <Text key={tick} style={styles.yAxisLabel}>{tick}</Text>
          ))}
        </View>

        <View
          style={styles.plotViewport}
          onLayout={(event) => {
            const nextHeight = event.nativeEvent.layout.height;
            if (nextHeight > 0 && Math.abs(nextHeight - plotHeight) > 1) setPlotHeight(Math.max(MIN_PLOT_HEIGHT, nextHeight));
          }}
        >
          <View
            style={[styles.plotArea, { height: plotHeight }]}
            onLayout={(event) => {
              const nextWidth = event.nativeEvent.layout.width;
              if (nextWidth > 0 && Math.abs(nextWidth - plotWidth) > 1) setPlotWidth(nextWidth);
            }}
          >
            <View style={[styles.vRef, { left: '60%' }]} />
            <View style={[styles.hRef, { top: '43%' }]} />

            {plotWidth > 0 && (
              <Svg width={plotWidth} height={plotHeight} style={StyleSheet.absoluteFill} pointerEvents="none">
                {positioned.map(({ point, x, y, originX, originY }) => {
                  const displaced = Math.hypot(x - originX, y - originY) > 2;
                  if (!displaced) return null;
                  return (
                    <Line
                      key={`connector-${point.label}`}
                      x1={originX}
                      y1={originY}
                      x2={x}
                      y2={y}
                      stroke="#9CA3AF"
                      strokeWidth={1}
                      strokeDasharray="2,2"
                    />
                  );
                })}
              </Svg>
            )}

            {positioned.map(({ point, size, x, y }, index) => {
              const isAtOrAboveThreshold = point.adoptionPct >= ADOPTION_COLOR_THRESHOLD;
              return (
              <Pressable
                key={point.label}
                onPress={() => setSelectedIndex((prev) => (prev === index ? null : index))}
                onHoverIn={() => setSelectedIndex(index)}
                onHoverOut={() => setSelectedIndex((prev) => (prev === index ? null : prev))}
                style={[
                  styles.bubble,
                  {
                    left: x,
                    top: y,
                    width: size,
                    height: size,
                    borderRadius: size / 2,
                    marginLeft: -size / 2,
                    marginTop: -size / 2,
                    backgroundColor: isAtOrAboveThreshold ? '#6EA8FF55' : '#FFA38A55',
                    borderColor: isAtOrAboveThreshold ? '#2B6CCF' : '#D65A3A',
                  },
                  // Lift the hovered/selected bubble (and its tooltip) above every other bubble in the plot.
                  selectedIndex === index && styles.bubbleActive,
                ]}
              >
                {showLabels && (
                  <Text
                    style={[
                      styles.bubbleLabel,
                      // Flip the label below the bubble whenever it's too close to the plot's top
                      // edge — otherwise it renders past the plotArea boundary and overlaps the
                      // quadrant header band text sitting just above it.
                      y - size / 2 < LABEL_HEIGHT_PX
                        ? { top: size / 2 + 4, bottom: undefined }
                        : { top: -size / 2 - 14 },
                    ]}
                    numberOfLines={1}
                    ellipsizeMode="tail"
                  >
                    {point.label}
                  </Text>
                )}

                {selectedIndex === index && (
                  <View style={[styles.tooltip, { left: size / 2 + 6 }, y > plotHeight / 2 ? styles.tooltipAbove : styles.tooltipBelow]}>
                    <Text style={styles.tooltipTitle}>{point.label}</Text>
                    <Text style={styles.tooltipRow}>{`Adoption: ${point.adoptionPct.toFixed(1)}%`}</Text>
                    <Text style={styles.tooltipRow}>{`KCPH Index: ${(point.kcphIndex ?? 0).toFixed(1)}`}</Text>
                    {typeof point.volume === 'number' && <Text style={styles.tooltipRow}>{`Volume: ${point.volume.toLocaleString()}`}</Text>}
                  </View>
                )}
              </Pressable>
              );
            })}
          </View>
        </View>
      </View>

      <View style={styles.headerBandsBottom}>
        <Text style={styles.headerBandText}>ENABLEMENT FIRST</Text>
        <Text style={styles.headerBandTextRight}>ADOPTED, RESULTS LAGGING</Text>
      </View>

      <View style={styles.axisRow}>
        <Text style={styles.axisLabel}>0%</Text>
        <Text style={styles.axisLabel}>25%</Text>
        <Text style={styles.axisLabel}>50%</Text>
        <Text style={styles.axisLabel}>75%</Text>
        <Text style={styles.axisLabel}>100%</Text>
      </View>
      <Text style={styles.xTitle}>Crewing Tool Adoption %</Text>
      <Text style={styles.footer}>Adoption is associated with a higher KCPH index in this scope. Association, not verified causation.</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#E5E7EB',
    padding: 16,
    flex: 1,
    minHeight: 320,
  },
  title: {
    fontSize: 16,
    lineHeight: 20,
    fontFamily: FontFamily.bold,
    color: '#1A1D21',
    fontWeight: '700',
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    columnGap: 10,
    width: '100%',
  },
  titleRow: {
    flexDirection: 'row',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    columnGap: 4,
    flexShrink: 1,
  },
  toggleWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  toggleLabel: {
    fontSize: 11,
    color: '#9CA3AF',
    fontWeight: '500',
  },
  toggleGroup: {
    flexDirection: 'row',
    borderRadius: 6,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: '#E5E7EB',
  },
  toggleBtn: {
    paddingHorizontal: 10,
    paddingVertical: 3,
    backgroundColor: '#FFFFFF',
  },
  toggleBtnActive: {
    backgroundColor: '#16A34A',
  },
  toggleBtnText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#9CA3AF',
  },
  toggleBtnTextActive: {
    color: '#FFFFFF',
  },
  subtitle: {
    fontSize: 12,
    lineHeight: 16,
    fontFamily: FontFamily.regular,
    fontWeight: '400',
    color: '#838383',
  },
  headerBands: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    marginBottom: 12,
  },
  headerBandsBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
  },
  headerBandText: {
    fontSize: 10,
    fontWeight: '700',
    color: '#6B7280',
  },
  headerBandTextRight: {
    fontSize: 10,
    fontWeight: '700',
    color: '#6B7280',
    textAlign: 'right',
  },
  plotViewport: {
    flex: 1,
    marginTop: 4,
  },
  plotRow: {
    flex: 1,
    flexDirection: 'row',
  },
  yAxisCol: {
    width: 30,
    height: MIN_PLOT_HEIGHT,
    justifyContent: 'space-between',
    paddingRight: 4,
  },
  yAxisLabel: {
    fontSize: 9,
    color: '#6B7280',
    textAlign: 'right',
  },
  plotArea: {
    height: MIN_PLOT_HEIGHT,
    minWidth: 500,
    borderLeftWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#D9E1ED',
    position: 'relative',
  },
  vRef: {
    position: 'absolute',
    top: 0,
    bottom: 0,
    borderLeftWidth: 1,
    borderLeftColor: '#9CA3AF',
    borderStyle: 'dashed',
  },
  hRef: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTopWidth: 1,
    borderTopColor: '#9CA3AF',
    borderStyle: 'dashed',
  },
  bubble: {
    position: 'absolute',
    width: BUBBLE_SIZE,
    height: BUBBLE_SIZE,
    borderRadius: BUBBLE_SIZE / 2,
    borderWidth: 2,
    marginLeft: -BUBBLE_SIZE / 2,
    marginTop: -BUBBLE_SIZE / 2,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
  },
  bubbleActive: {
    zIndex: 100,
    elevation: 12,
  },
  bubbleLabel: {
    position: 'absolute',
    top: -16,
    fontSize: 9,
    color: '#374151',
    fontWeight: '600',
    backgroundColor: 'rgba(255,255,255,0.85)',
    paddingHorizontal: 3,
    borderRadius: 3,
    maxWidth: 84,
  },
  tooltip: {
    position: 'absolute',
    left: BUBBLE_SIZE / 2 + 6,
    width: 132,
    backgroundColor: '#1E2127',
    borderRadius: 6,
    paddingHorizontal: 8,
    paddingVertical: 6,
    zIndex: 50,
    elevation: 12,
    shadowColor: '#000',
    shadowOpacity: 0.25,
    shadowOffset: { width: 0, height: 2 },
    shadowRadius: 6,
  },
  tooltipAbove: {
    bottom: BUBBLE_SIZE / 2,
  },
  tooltipBelow: {
    top: BUBBLE_SIZE / 2,
  },
  tooltipTitle: {
    fontSize: 11,
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: 2,
  },
  tooltipRow: {
    fontSize: 10,
    color: '#E5E7EB',
    lineHeight: 14,
  },
  axisRow: {
    marginTop: 4,
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingLeft: 34,
    minWidth: 500,
  },
  axisLabel: {
    fontSize: 10,
    color: '#6B7280',
  },
  xTitle: {
    marginTop: 4,
    fontSize: 10,
    color: '#4B5563',
    textAlign: 'center',
  },
  footer: {
    marginTop: 6,
    fontSize: 9,
    color: '#6B7280',
  },
  emptyState: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  emptyText: {
    fontSize: 14,
    color: '#9CA3AF',
    fontWeight: '500',
  },
});

export default ExecutionQuadrantCard;
